import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { TagGq } from '../../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import { noTagPlaceholder } from './noTagPlacerholder';
import { CircularProgress } from '@material-ui/core';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';

export interface ColoredTagChipProps extends React.HTMLAttributes<HTMLDivElement> {
  tagGroup: TagGroupGq;
  tag?: TagGq;
  loading?: boolean;
  showClose?: boolean;
  stopPropagation?: boolean;
  ellipsis?: boolean;
}

const ColoredTagChip: React.FC<ColoredTagChipProps> = React.forwardRef<
  HTMLDivElement,
  ColoredTagChipProps
>(
  (
    {
      tag,
      tagGroup,
      onClick,
      loading,
      showClose = true,
      stopPropagation = true,
      ellipsis = false,
      ...props
    },
    ref
  ) => {
    // Calculate color index
    let colorIndex = useMemo(() => {
      const sortedTags = orderBy(tagGroup?.tags, [(t) => t.id]);
      return sortedTags.map((t) => t.id).indexOf(tag?.id ?? -1);
    }, [tag, tagGroup]);

    // Get styles
    const classes: string[] = [
      'text-voxel-blue-600 bg-voxel-blue-50/80',
      'text-voxel-blue-600 bg-voxel-blue-100/70',
      'text-voxel-green-900 bg-voxel-green-100/50',
      'text-voxel-green-900 bg-voxel-green-300/50',
      'text-voxel-orange-900 bg-voxel-orange-100/60',
      'text-voxel-orange-900 bg-voxel-orange-300/40',
      'text-voxel-gray-800 bg-voxel-gray-100',
      'text-voxel-gray-800 bg-voxel-gray-200',
    ];
    let colorClass = classes[colorIndex % classes.length];

    // Render
    const label = tag?.name ?? noTagPlaceholder;
    const ellipsisLength = 19;
    const useEllipsis = label?.length > ellipsisLength + 2;
    if (colorIndex < 0) {
      return <>{label}</>;
    }

    return (
      <VoxelTooltipShort message={label} disabled={!useEllipsis}>
        <div
          ref={ref}
          className="bg-white rounded-md inline"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            stopPropagation && e.stopPropagation();
            onClick && onClick(e);
          }}
          {...props}
        >
          <div
            className={`
          ${colorClass} 
          ${showClose && 'pl-2 pr.0.5'} 
          ${!showClose && 'px-2'} 
          rounded-md inline-flex flex-row items-center gap-1
          `}
          >
            <span
              className={`py-0.5 text-left text-[13px] ${
                ellipsis ? 'max-w-[128px]  truncate' : ''
              }`}
            >
              {useEllipsis ? label?.slice(0, ellipsisLength) + '...' : label}
            </span>
            {loading && <CircularProgress size={12} className="opacity-70" />}
            {showClose && (
              <span className="rounded-md hover:bg-black/10  w-[17px] h-[17px] flex items-center justify-center opacity-30 hover:opacity-60 cursor-pointer">
                <FontAwesomeIcon icon={faTimes} fontSize={10} />
              </span>
            )}
          </div>
        </div>
      </VoxelTooltipShort>
    );
  }
);

export default ColoredTagChip;
